/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/FlaggedSubmissions/FlaggedSubmissions.tsx.
 */

import React, { useEffect, useMemo, useState, useCallback } from "react";
import "../Contributor.css";
import "./FlaggedSubmissions.css";
import DataTable, { Column } from "../../../../components/common/DataTable";
import AnalysisBanner from "../../../../components/common/AnalysisBanner";
import { getFlaggedSubmissions } from "../../../../services/submissionService";
import {
  formatReviewStatusLabel,
  normalizeReviewStatus,
  reviewStatusBadgeSuffix,
} from "../../../../constants/reviewStatus";

interface FlaggedSubmissionsProps {
  onResumeSubmission: (
    id: string,
    options?: { mode: "beginning" | "current"; currentStage?: string }
  ) => void;
}

/** API may nest reasons on submission, background, flag, or recent reviews */
function extractDisplayReason(row: Record<string, unknown>): string {
  const r = row as {
    flagReason?: string;
    flag?: { reason?: string };
    background?: { reviewReason?: string; flagReason?: string };
    recentReviews?: Array<{ comment?: string; reason?: string }>;
    reviews?: Array<{ comment?: string; reason?: string }>;
  };
  const list = r.recentReviews ?? r.reviews;
  const last =
    Array.isArray(list) && list.length > 0 ? list[list.length - 1] : undefined;
  const text =
    r.flagReason ??
    r.flag?.reason ??
    r.background?.reviewReason ??
    r.background?.flagReason ??
    last?.comment ??
    last?.reason ??
    "";
  return String(text).trim();
}

function submissionRowId(row: Record<string, unknown>): string {
  const r = row as { id?: string; submissionId?: string };
  return String(r.id ?? r.submissionId ?? "");
}

const FlaggedSubmissions: React.FC<FlaggedSubmissionsProps> = ({
  onResumeSubmission,
}) => {
  const [submissions, setSubmissions] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getFlaggedSubmissions();
      const body = res.data as { success?: boolean; data?: unknown[] };
      const list = Array.isArray(body?.data) ? body.data : [];
      setSubmissions(list as Record<string, unknown>[]);
    } catch (err) {
      console.error("Failed to load flagged submissions", err);
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatStage = (stage: string) => {
    if (!stage) return "—";
    return stage.replace(/_/g, " ");
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return submissions;
    const q = search.toLowerCase().trim();
    return submissions.filter((row) => {
      const code = String(
        (row as { submissionCode?: string }).submissionCode ?? ""
      ).toLowerCase();
      const reason = extractDisplayReason(row).toLowerCase();
      const rs = formatReviewStatusLabel(
        (row as { reviewStatus?: string }).reviewStatus
      ).toLowerCase();
      return code.includes(q) || reason.includes(q) || rs.includes(q);
    });
  }, [submissions, search]);

  const columns: Column<Record<string, unknown>>[] = useMemo(
    () => [
      {
        key: "submissionCode",
        label: "Submission code",
        render: (item) =>
          String((item as { submissionCode?: string }).submissionCode ?? "—"),
      },
      {
        key: "reviewStatus",
        label: "Review status",
        render: (item) => {
          const raw = (item as { reviewStatus?: string }).reviewStatus;
          const n = normalizeReviewStatus(raw);
          return (
            <span className={`flagged-status-pill ${reviewStatusBadgeSuffix(n)}`}>
              {formatReviewStatusLabel(raw)}
            </span>
          );
        },
      },
      {
        key: "currentStage",
        label: "Current stage",
        render: (item) =>
          formatStage(
            String((item as { currentStage?: string }).currentStage ?? "")
          ),
      },
      {
        key: "reason",
        label: "Last note / reason",
        render: (item) => {
          const t = extractDisplayReason(item);
          if (!t) return "—";
          return t.length > 120 ? `${t.slice(0, 117)}…` : t;
        },
      },
      {
        key: "updatedAt",
        label: "Updated",
        render: (item) => {
          const u = (item as { updatedAt?: string }).updatedAt;
          return u ? formatDate(u) : "—";
        },
      },
    ],
    []
  );

  const renderActions = (item: Record<string, unknown>) => (
    <button
      type="button"
      className="flagged-resume-btn"
      onClick={() => onResumeSubmission(submissionRowId(item))}
    >
      Resume
    </button>
  );

  return (
    <div className="flagged-submissions-container">
      <AnalysisBanner title="Flagged Datasets" />

      <p className="flagged-lede">
        These submissions were flagged by an admin or expert and need your
        attention. Open the wizard, make corrections, and move stages as usual —
        your review status will move to <strong>Revised</strong> after you
        update while flagged.
      </p>

      <div className="flagged-table-wrap">
        <DataTable
          data={filtered}
          columns={columns}
          loading={loading}
          searchPlaceholder="Search by code, status, or reason…"
          onSearch={setSearch}
          searchValue={search}
          pagination
          itemsPerPage={10}
          renderActions={renderActions}
          emptyMessage="No flagged datasets right now."
        />
      </div>
    </div>
  );
};

/** React component — Flagged Submissions. */
export default FlaggedSubmissions;
